/// <reference lib="dom" />
/** Browser-native equivalents of the next/navigation hooks used by admin page components, for Vite-built MFE bundles. Consumed via Vite resolve.alias -- each MFE's vite.config.ts maps "next/navigation" to this file. */

import { useState, useCallback, useMemo } from 'react';
import { DOM_EVENTS } from '@federa/const/websocket-actions-dom';
import { getShellNavigate } from '@federa/components/common/federa-shell-reader';
import { globalKey } from '@federa/const/global-namespace';
import { isPlainObject } from '@federa/const/record-guards';
import { useMfeNavigation } from '../hooks/useMfeNavigation.ts';
import { historyBack } from './history-back.ts';

const GLOBAL_KEY_ROUTE_PARAMS = 'RouteParams';

/**
 * The per-navigation options bag, named for the member `next/navigation` declares it under so the shape parity below
 * reads against upstream rather than against four re-spellings. The shim accepts and DISCARDS it: browser History has
 * no scroll-restoration knob to honour.
 *
 * @reader-external next/navigation NavigateOptions
 */
type NavigateOptions = { scroll?: boolean };

/** The one navigation signature `push` and `replace` share. */
type NavigateFn = (url: string, options?: NavigateOptions) => void;

/**
 * Cross-page navigation goes through the brand-prefixed shell global's `.navigate`; in-page URL updates (query params) use pushState
 * directly.
 *
 * SHAPE PARITY is the contract: the whole interface stands in for `next/navigation`'s `AppRouterInstance` so a component written against the
 * real router drops in unchanged. Members exist because the FRAMEWORK declares them, not because a repo call site reads them back.
 *
 * @reader-external next/navigation AppRouterInstance
 */
interface MfeRouter {
	push: NavigateFn;
	replace: NavigateFn;
	back: () => void;
	forward: () => void;
	refresh: () => void;
	/** Shape parity with `next/navigation`'s `AppRouterInstance`, so a component written against the real router drops in unchanged. The consumer is the FRAMEWORK contract, not a repo call site -- a reachability pass reports it dead; deleting it narrows the shim below the interface it stands in for. */
	prefetch: () => void;
}

/**
 * Resolves a navigation target against the current href, normalizing the one shape URL serialization gets wrong for a
 * History write: a `'?'`-only url (every param back at its default) resolves with the empty-query flag set, so
 * `toString()` keeps a bare trailing `?` on the address bar forever after. Strip it -- the two spellings are equivalent
 * per the URL spec, and the guards keep a real query (`?q=1`) and a hash that merely ends in `?` untouched.
 */
export function resolveNavigationHref(url: string, baseHref: string): string {
	const target = new URL(url, baseHref);
	const href = target.toString();
	if (target.search === '' && target.hash === '' && href.endsWith('?')) return href.slice(0, -1);
	return href;
}

/**
 * The one navigation body `push` and `replace` share, parameterized by which History method writes the entry.
 *
 * An in-page URL change (query/hash only) always goes through History directly -- routing it to the shell would remount
 * the MFE for what is a same-page state change. A cross-page URL prefers the shell's `navigate`, falling back to
 * History: standalone SPAs have no shell global, and without the fallback the optional call no-ops and clicks navigate
 * nowhere.
 */
function navigateVia(historyMethod: 'pushState' | 'replaceState', url: string): void {
	const isInPage = url.startsWith('?') || url.startsWith('#');
	if (!isInPage) {
		const navigate = getShellNavigate();
		if (navigate) {
			navigate(url);
			return;
		}
	}
	window.history[historyMethod](null, '', resolveNavigationHref(url, window.location.href));
	void window.dispatchEvent(new Event(DOM_EVENTS.MFE_URL_CHANGE));
}

export function useRouter(): MfeRouter {
	const push = useCallback((url: string, _options?: NavigateOptions): void => {
		navigateVia('pushState', url);
	}, []);

	const replace = useCallback((url: string, _options?: NavigateOptions): void => {
		navigateVia('replaceState', url);
	}, []);

	return useMemo(
		() => ({
			push,
			replace,
			back: historyBack,
			forward: () => window.history.forward(),
			refresh: () => window.location.reload(),
			prefetch: () => {},
		}),
		[push, replace],
	);
}

/**
 * The shim's URL-change subscription for its three readers, delegated to `useMfeNavigation`, which owns the SET of DOM
 * signals that mean "the in-app URL changed".
 *
 * It stays a NAMED local rather than folding away entirely: it is the three readers' single entry point, and
 * `QUAL-MFE-URL-CHANGE-SHIM-BYPASS-001` names it by that name as one of the two event readers a hand-rolled History
 * write bypasses. What it no longer carries is a SECOND copy of the signal set -- the `popstate` +
 * `DOM_EVENTS.MFE_URL_CHANGE` pair was registered here AND, verbatim, in `useMfeNavigation`. Two subscriber lists over
 * one set is the divergence: a change to the set lands on one and not the other, and the surface that missed it
 * silently stops re-rendering on half its navigations -- which is the exact failure `useMfeNavigation`'s docstring says
 * registering the pair together is there to prevent. That argument is what makes ONE list the correct shape; it was
 * being made in a file that then kept its own.
 *
 * Behaviour is unchanged and the caller contract RELAXES: `useEventListener` required a memoized `handler` and
 * re-subscribed whenever its identity changed, while `useMfeNavigation` reads the handler through `useLatestRef` and
 * registers once for the component's lifetime. Both readers below already pass a `useCallback` with empty deps, so no
 * live subscription changes.
 */
function useUrlChangeEffect(handler: () => void): void {
	useMfeNavigation(handler);
}

/** Live: re-reads on every URL change. */
export function useSearchParams(): URLSearchParams {
	const [params, setParams] = useState(() => new URLSearchParams(window.location.search));

	const handler = useCallback((): void => setParams(new URLSearchParams(window.location.search)), []);
	useUrlChangeEffect(handler);

	return params;
}

export function usePathname(): string {
	const [pathname, setPathname] = useState(() => window.location.pathname);

	const handler = useCallback((): void => setPathname(window.location.pathname), []);
	useUrlChangeEffect(handler);

	return pathname;
}

/** A Vite-built SPA has no route tree for `useParams()` to resolve against, so the SPA shell parses `window.location` and calls this BEFORE rendering a `useParams` consumer, keying that component by the param value so a change remounts it. */
export function setMfeRouteParams(params: Record<string, string>): void {
	Reflect.set(window, globalKey(GLOBAL_KEY_ROUTE_PARAMS), params);
}

function readMfeRouteParams(): Record<string, string> {
	const params: unknown = Reflect.get(window, globalKey(GLOBAL_KEY_ROUTE_PARAMS));
	return isPlainObject(params) ? (params as Record<string, string>) : {};
}

/** Live-updates on URL changes; hosts that remount per-param rely only on the lazy initializer. */
export function useParams(): Record<string, string> {
	const [params, setParams] = useState(readMfeRouteParams);

	const handler = useCallback((): void => setParams(readMfeRouteParams()), []);
	useUrlChangeEffect(handler);

	return params;
}
